/**
 * Types and utilities from Zero's IVM (Incremental View Maintenance).
 *
 * Defined locally — Zero's internal paths are not resolvable by Vite
 * (package exports don't expose @rocicorp/zero/out/zql/src/ivm/*).
 * Shapes match Zero's IVM contracts used by zero-solid and zero-vue.
 *
 * Used by: ReactMessageView (push-based View for React).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Row = Record<string, any>;

export type Node = {
  readonly row: Row;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly relationships: Record<string, any>;
};

export type AddChange = { type: "add"; node: Node };
export type RemoveChange = { type: "remove"; node: Node };
export type EditChange = { type: "edit"; node: Node; oldNode: Node };
export type ChildChange = {
  type: "child";
  node: Node;
  child: { relationshipName: string; change: Change };
};
export type Change = AddChange | RemoveChange | EditChange | ChildChange;

/**
 * Zero's IVM operators push tuple-shaped changes to Output.push():
 *   [0, node, null]      → add
 *   [1, node, null]      → remove
 *   [2, node, oldNode]   → edit
 *   [3, node, childData] → child
 *
 * Both reference Views — `ArrayView.changeToViewChange` and
 * `SolidView.materializeRelationships` — convert this to the
 * object-shaped {type, node, ...} form before dispatch. Without
 * that step, a switch on `change.type` defaults (tuples have no
 * `.type` property) and every change is dropped silently.
 *
 * Returns null if the input is neither a recognized tuple nor an
 * already-object-shaped change — caller can log it for diagnosis.
 */
export function materializeChange(rawChange: unknown): Change | null {
  if (Array.isArray(rawChange)) {
    const tuple = rawChange as readonly unknown[];
    const typeIdx = tuple[0];
    const node = tuple[1] as Node;
    switch (typeIdx) {
      case 0:
        return { type: "add", node };
      case 1:
        return { type: "remove", node };
      case 2:
        return { type: "edit", node, oldNode: tuple[2] as Node };
      case 3:
        return {
          type: "child",
          node,
          child: tuple[2] as ChildChange["child"],
        };
      default:
        return null;
    }
  }
  if (
    typeof rawChange === "object" &&
    rawChange !== null &&
    "type" in (rawChange as Record<string, unknown>)
  ) {
    return rawChange as Change;
  }
  return null;
}

export interface InputBase {
  getSchema(): unknown;
  destroy(): void;
}

export interface Input extends InputBase {
  setOutput(output: Output): void;
  fetch(req: Record<string, unknown>): Iterable<Node | "yield">;
}

export interface Output {
  push(change: Change, pusher: InputBase): Iterable<"yield">;
}

/** Skip 'yield' values from Zero's Input.fetch() iterator. */
export function* skipYields(stream: Iterable<Node | "yield">): Iterable<Node> {
  for (const item of stream) {
    if (item !== "yield") yield item;
  }
}
